import React, {useEffect} from "react";
import {
  FunctionComponent,
  useState,
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
} from "react";
import ButtonType from "../../../models/ButtonType";
import { useGetCooperationAreas } from "../../../hooks/useGetCooperationAreas";
import { useGetCooperationSubAreas } from "../../../hooks/useGetCooperationSubAreas";
import {
  CooperationArea,
  CooperationSubArea,
  Coordinates,
  MapSettingsResponse,
} from "../../../api/Client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { localization } from "../../../localization/localizedStrings";
import useStore from "../../../hooks-store/store";
import Position from "../../../models/Position";
import SelectBox from "../../ui/SelectBox";
import SelectBoxItem from "../../../models/SelectBoxItem";
import {
  Divider,
  SegmentControlSingleChangeEvent,
  SegmentedControl,
  SingleSelectChangeEvent,
  Text,
  useGoogleMapsService,
} from "@velocity/ui";
import AddressAutoComplete from "./AddressAutoComplete";
import CustomButton from "../../ui/CustomButton";
import MapFilterValues from "../../../models/MapFilterValues";
import {SearchIcon, LocationIcon, TrashIcon} from "@velocity/icons/system";
import { useGetBrands } from "../../../hooks/useGetBrands";

interface MapFilterProps {
  id: string;
  defaultFilter?: MapFilterValues;
  countryId?: number;
  languageId?: number;
  isMobile?: boolean;
  onReset?: () => void;
  onFilterSummaryChange?: (summary: string) => void;
}

const MapFilter: FunctionComponent<MapFilterProps> = ({
  id,
  defaultFilter,
  countryId,
  languageId,
  isMobile,
  onReset,
  onFilterSummaryChange,
}: MapFilterProps) => {
  const googleMapsService = useGoogleMapsService();
  const [loading, setLoading] = useState<boolean>(false);
  const [appSettingsStore, ] =
    useStore<MapSettingsResponse | null>("appSettings");
  const [, setBrandStore] = useStore<string | undefined>("brand");
  const [, setOpenFilterDrawerStore] =
    useStore<boolean>("openFilterDrawer");
  const [errorSelection, setErrorSelection] = useState<boolean>(false);
  const [errorSelectionMessage, setErrorSelectionMessage] =
    useState<string>("");


  //find default coordinates in app settings based on default country
  const foundCoordinates: Coordinates[] | undefined =
    appSettingsStore?.data?.coordinates?.filter(
      (item) => item.countryCode === appSettingsStore?.data?.defaultCountry
    );
  const defaultCoordinates: Coordinates | null =
    foundCoordinates?.length! > 0 ? foundCoordinates![0] : null;

  const defaultPosition: Position = {
    lat: defaultFilter?.position?.lat
      ? defaultFilter?.position?.lat
      : parseFloat(defaultCoordinates?.lat as string) ?? 50.0762369,
    lng: defaultFilter?.position?.lng
      ? defaultFilter?.position?.lng
      : parseFloat(defaultCoordinates?.lng as string) || 14.4502317,
  };
  const defaultDistance: number = 80000;

  const [positionStore, setPositionStore] = useStore<Position>(
    "position",
    defaultPosition
  );

const [distanceStore, setDistanceStore] = useStore<number>(
    "distance",
    defaultDistance
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [selectedCooperationArea, setSelectedCooperationArea] =
    useState<number>(defaultFilter?.selectedCoopArea ?? 0);
  const [selectedCooperationSubArea, setSelectedCooperationSubArea] =
    useState<number>(defaultFilter?.selectedCoopSubArea ?? 0);
  const [currentPosition, setCurrentPosition] =
    useState<Position>(defaultPosition);
  const [selectedAddress, setSelectedAddress] = useState<string>(
    defaultFilter?.address ?? ""
  );
  const [cooperationAreas, setCooperationAreas] = useState<
    Array<SelectBoxItem> | undefined
  >([]);
  const [cooperationSubAreas, setCooperationSubAreas] = useState<
    Array<SelectBoxItem> | undefined
  >([]);

  const { data: cooperationAreasData, isSuccess: cooperationAreasSuccess } =
    useGetCooperationAreas(countryId, languageId);
  const {
    data: cooperationSubAreasData,
    isSuccess: cooperationSubAreasSuccess,
  } = useGetCooperationSubAreas(countryId, languageId);
  const { data: brandsData, isSuccess: brandsSuccess } = useGetBrands(
    countryId,
    languageId
  );

  const getDropDownItems = (data: any): Array<SelectBoxItem> | undefined => {
    return data?.map((item: any) => {
      if (item instanceof CooperationArea) {
        return {
          title: item.name!,
          value: item.id?.toString()!,
        } as SelectBoxItem;
      } else if (item instanceof CooperationSubArea) {
        return {
          title: item.name!,
          value: item.id?.toString()!,
          parentId: item?.areaId ?? 0,
        } as SelectBoxItem;
      }
    });
  };

  const handleCooperationAreaChange = (event: SingleSelectChangeEvent) => {
    const selectedCooperationArea = parseInt(event.target.value);
    setSelectedCooperationArea(selectedCooperationArea);
    setSelectedCooperationSubArea(0);

    const subAreas = cooperationSubAreasData?.data?.filter(
      (subArea) =>
        subArea.areaId === selectedCooperationArea ||
        selectedCooperationArea === 0
    );

    const subAreasItems = getDropDownItems(subAreas);
    setCooperationSubAreas(
      subAreasItems?.filter((item) => item.title !== "" && item.title !== null)
    );

    const params = new URLSearchParams(searchParams);
    params.delete("coopSubArea");
    setSearchParams(params);
  };

  const handleCooperationSubAreaChange = (event: SingleSelectChangeEvent) => {
    setSelectedCooperationSubArea(parseInt(event.target.value));
  };

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedAddress(event.target.value);
  };

  const handleOnFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCooperationArea != 0) {
      let params = serializeFormQuery(event.target);

      if (positionStore?.lat && positionStore?.lng) {
        params.append("lat", positionStore?.lat?.toString());
        params.append("lng", positionStore?.lng?.toString());
      }

      if (distanceStore) {
        //params.append("distance", distanceStore.toString());
      }

      setSearchParams(params);
      //navigate(window.location.href); //HOTFIX: force navigation to refresh the page (search params are not updated without this)

      setErrorSelection(false);
      setErrorSelectionMessage("");
    } else {
      setErrorSelection(true);
      setErrorSelectionMessage(localization.selectionError);
    }

    if (isMobile) {
      setOpenFilterDrawerStore(false);
    }
  };

  const handleMyLocationSuccess: PositionCallback = async ({ coords }) => {
    const { latitude: lat, longitude: lng } = coords;
    const locationByCoords = await googleMapsService.findPlaceByLocation({
      lat,
      lng,
    });

    setPositionStore({ lat, lng });
    setSelectedAddress(locationByCoords.formattedAddress);
    setLoading(false);
  };


  const handleMyLocationError: PositionErrorCallback = (positionError) => {
    console.log(positionError);
    setSelectedAddress("");
    setLoading(false);
  };

    const handleOnMyLocationClick = (event: MouseEvent<HTMLButtonElement>) => {
    setLoading(true);

    navigator?.geolocation.getCurrentPosition(
      handleMyLocationSuccess,
      handleMyLocationError
    );
  };

  const handleOnReset = () => {
    setSelectedCooperationArea(0);
    setSelectedCooperationSubArea(0);
    setSelectedAddress("");
    setPositionStore(defaultPosition);
    setDistanceStore(defaultDistance);
    setErrorSelection(false);
    setErrorSelectionMessage("");
    setSearchParams(new URLSearchParams());
    setCooperationSubAreas([]);
    if (isMobile) {
      setOpenFilterDrawerStore(false);
    }
    if (onReset) onReset();
  };

  const serializeFormQuery = (form: EventTarget | null) => {
    let params = new URLSearchParams();
    if (form) {
      const formElements = (form as HTMLFormElement).elements;
      for (let i = 0; i < formElements.length; i++) {
        const element = formElements[i] as HTMLInputElement;
        if (element.value && element.value !== "0") {
          if (element.id) {
            params.append(element.id, element.value);
          } else if (element.name) {
            //id is not working on select elements, just name
            params.append(element.name, element.value);
          }
        }
      }
    }
    return params;
  };


  const distances = [
    { value: 5000, title: "5 km" },
    { value: 10000, title: "10 km" },
    { value: 20000, title: "20 km" },
    { value: 50000, title: "50 km" },
  ];

  const [selectedDistance, setSelectedDistance] = useState<number>(
    defaultFilter?.distance ? defaultFilter.distance : 80000
  );

  useEffect(() => {
    setDistanceStore(selectedDistance)
  }, [selectedDistance]);


  const handleDistanceChange = (event: SegmentControlSingleChangeEvent) => {
    setSelectedDistance(parseInt(event.target.value));
  };

  const loadBrand = useCallback(() => {
    if (brandsSuccess) {
      const defaultBrand =
        defaultFilter?.brands && defaultFilter?.brands.length > 0
          ? defaultFilter?.brands[0]
          : 0;
      const foundBrand = brandsData?.data?.find(
        (brand) => brand.id === defaultBrand
      );
      setBrandStore(foundBrand?.name);
    }
  }, [brandsData, brandsSuccess, defaultFilter]);

  const loadCooperationAreas = useCallback(() => {
    if (cooperationAreasSuccess && cooperationSubAreasSuccess) {
      // Get all areaIds from subareas with non-empty name
      const validSubAreaIds = new Set(
        cooperationSubAreasData?.data
          ?.map((subArea: CooperationSubArea) => subArea.areaId)
      );

      // Filter areas: must be present in validSubAreaIds and have non-empty name
      const filteredCooperationAreas: CooperationArea[] =
        cooperationAreasData?.data?.filter(
          (area: CooperationArea) =>
            validSubAreaIds.has(area.id) && area.name && area.name !== ""
        ) ?? [];

      const areasItems = getDropDownItems(filteredCooperationAreas);

      areasItems?.unshift({
        title: localization.defaultSelectValue,
        value: "0",
      });
      setCooperationAreas(areasItems);
    }
  }, [cooperationAreasSuccess, cooperationAreasData, cooperationSubAreasSuccess, cooperationSubAreasData, defaultFilter]);

  const loadCooperationSubAreas = useCallback(() => {
    if (cooperationSubAreasSuccess) {
      const cooperationSubAreas: CooperationSubArea[] | null | undefined =
        cooperationSubAreasData?.data;
      let filteredCooperationSubAreas: CooperationSubArea[] | undefined = [];
      if (selectedCooperationArea && selectedCooperationArea !== 0) {
        filteredCooperationSubAreas = cooperationSubAreas?.filter((subArea) => {
          return selectedCooperationArea === subArea.areaId;
        });
      }

      const subAreaItems = getDropDownItems(filteredCooperationSubAreas);

      //some items are empty, remove them
      setCooperationSubAreas(
        subAreaItems?.filter((item) => item.title !== "" && item.title !== null)
      );
    }
  }, [cooperationSubAreasSuccess, cooperationSubAreasData, selectedCooperationArea]);

  useEffect(() => {
    const hasSubAreas = !!cooperationSubAreas && cooperationSubAreas.length > 0;
    const subAreaExists = hasSubAreas
      ? cooperationSubAreas.some(
          (item) => item.value === selectedCooperationSubArea.toString()
        )
      : false;
    const shouldReset =
      selectedCooperationSubArea !== 0 && (!hasSubAreas || !subAreaExists);

    if (shouldReset) {
      setSelectedCooperationSubArea(0);
      const params = new URLSearchParams(searchParams);
      params.delete("coopSubArea");
      setSearchParams(params);
    }
  }, [cooperationSubAreas, selectedCooperationSubArea, searchParams, setSearchParams]);

  useEffect(() => {
    loadCooperationAreas();
    loadCooperationSubAreas();
    loadBrand();
  }, [loadCooperationAreas, loadCooperationSubAreas, loadBrand]);


    // Build and emit a localized summary of current selections to the parent
    const buildFilterSummary = (): string => {
        const parts: string[] = [];

        parts.push(localization.currentFilter);


        const distanceTitle = distances.find((d) => d.value === selectedDistance)?.title;
        if (distanceTitle) {
            parts.push(distanceTitle);
        }

        const areaTitle = cooperationAreas?.find((i) => i.value === selectedCooperationArea.toString())?.title;
        if (areaTitle && selectedCooperationArea !== 0) {
            parts.push(areaTitle);
        }

        const subAreaTitle = cooperationSubAreas?.find((i) => i.value === selectedCooperationSubArea.toString())?.title;
        if (subAreaTitle && selectedCooperationSubArea !== 0) {
            parts.push(subAreaTitle);
        }

        if (selectedAddress) {
            parts.push(selectedAddress);
        }

        return parts.join(" | ");
    };

    useEffect(() => {
        if (onFilterSummaryChange) {
            onFilterSummaryChange(buildFilterSummary());
        }
    }, [
        selectedDistance,
        selectedCooperationArea,
        selectedCooperationSubArea,
        selectedAddress,
        cooperationAreas,
        cooperationSubAreas,
        onFilterSummaryChange,
    ]);


    return (
    <>
      <div style={{ height: "100%" }}>
        <form
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
          onSubmit={handleOnFormSubmit}
        >
          <div style={{ width: "100%", flexGrow: 1 }}>
              {/*<h4>{localization.radius}</h4>
            <Text>{localization.radiusDescription}</Text>
            <SegmentedControl
              kind="form"
              id="distance"
              defaultValue="5000"
              aria-label="Distance selector"
              className="mb-1 mt-1"
              value={selectedDistance.toString()}
              onChange={handleDistanceChange}
            >
              {distances.map((item) => (
                <SegmentedControl.Option
                  key={item.value}
                  value={item.value.toString()}
                >
                  {item.title}
                </SegmentedControl.Option>
              ))}
            </SegmentedControl>
            <Divider />*/}
            <h4 style={{ marginTop: "1rem" }}>{localization.filterDescription}</h4>
            <SelectBox
              label={localization.services}
              id="coopArea"
              items={cooperationAreas}
              onChange={handleCooperationAreaChange}
              value={selectedCooperationArea.toString()}
              required={true}
              isError={errorSelection}
              errorMessage={errorSelectionMessage}
              className="mb-1 mt-1"
            />
            <SelectBox
              label={localization.serviceType}
              id="coopSubArea"
              items={cooperationSubAreas}
              onChange={handleCooperationSubAreaChange}
              value={selectedCooperationSubArea.toString()}
              disabled={/*cooperationSubAreas?.length === 0*/false}
              required={false}
              className="mb-1"
            />
            <AddressAutoComplete
              label={localization.address}
              id={"address"}
              placeholder={localization.address}
              value={selectedAddress}
              defaultValue={selectedAddress}
              className="mb-1"
              onChange={handleAddressChange}
            />
          </div>
            <Divider />
            <div className="mt-1" style={{ minWidth: "330px", display: "flex", justifyContent: "space-between", flexWrap: "wrap"}}>
                <CustomButton
                variant={"primary-outlined"}
                type={ButtonType.Button}
                text={loading ? "Loading..." : localization.myLocation}
                onClick={handleOnMyLocationClick}
                startIcon={LocationIcon}
              />
              <CustomButton
                  variant={"primary-outlined"}
                  type={ButtonType.Button}
                  text={localization.deleteFilter}
                  onClick={handleOnReset}
                  startIcon={TrashIcon}
              />
            </div>
            <div className="mt-1" style={{ minWidth: "330px" }}>
                <CustomButton
                    variant={"primary-filled"}
                    type={ButtonType.Submit}
                    text={localization.search}
                    startIcon={SearchIcon}
                    fullWidth={true}
                />
            </div>
        </form>
      </div>
    </>
  );
};

export default MapFilter;
